import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import i18n from '@/i18n';
import { resolveRegion, SupportedRegion, SupportedCurrency } from '@/lib/pricing';

declare global {
  interface Window {
    __GEO_INITIAL__?: {
      country: string;
      isNorthAmerica: boolean;
      isSouthAfrica: boolean;
      vatApplies: boolean;
      language: string;
      locale: string;
      currency: 'ZAR' | 'USD';
      currencySymbol: 'R' | '$';
    };
  }
}

/**
 * Geolocation & Pricing Rules:
 * - "za" (South Africa): ZAR ("R"), isSouthAfrica: true, vatApplies: true.
 * - "na", "bw", "ls", "sz" (Namibia, Botswana, Lesotho, Eswatini): ZAR ("R"), isSouthAfrica: false, vatApplies: false.
 * - All other countries / global: USD ("$"), isSouthAfrica: false, vatApplies: false.
 * - Language: UK English globally, EXCEPT in North America (US, Canada, Mexico) which displays US English.
 */
export const NORTH_AMERICA_REGIONS = ['us', 'ca', 'mx'] as const;
export const SOUTH_AFRICA_REGIONS = ['za'] as const;
export const NEIGHBOUR_ZAR_REGIONS = ['na', 'bw', 'ls', 'sz'] as const;

export function isNorthAmerica(countryCode?: string | null, continentCode?: string | null): boolean {
  if (continentCode && continentCode.toUpperCase() === 'NA') return true;
  if (!countryCode) return false;
  const clean = countryCode.toLowerCase().trim();
  return (
    NORTH_AMERICA_REGIONS.includes(clean as any) ||
    clean === 'usa' ||
    clean === 'can' ||
    clean === 'mex' ||
    clean.startsWith('us-') ||
    clean.startsWith('ca-') ||
    clean.startsWith('mx-')
  );
}

export function isSouthAfrica(countryCode?: string | null): boolean {
  if (!countryCode) return false;
  const clean = countryCode.toLowerCase().trim();
  return clean === 'za' || clean === 'rsa' || clean.endsWith('-za') || clean.endsWith('_za');
}

export function isZarCountry(countryCode?: string | null): boolean {
  if (!countryCode) return false;
  const clean = countryCode.toLowerCase().trim();
  return (
    isSouthAfrica(clean) ||
    ['na', 'bw', 'ls', 'sz', 'nam', 'bwa', 'lso', 'swz'].includes(clean) ||
    clean.endsWith('-na') || clean.endsWith('_na') ||
    clean.endsWith('-bw') || clean.endsWith('_bw') ||
    clean.endsWith('-ls') || clean.endsWith('_ls') ||
    clean.endsWith('-sz') || clean.endsWith('_sz')
  );
}

export function isVatCountry(countryCode?: string | null): boolean {
  return isSouthAfrica(countryCode);
}

export function isSouthernAfrica(code?: string | null): boolean {
  return isZarCountry(code);
}

interface RegionalContent {
  regionCode: string;
  headline: string;
  description: string;
}

export type { SupportedRegion, SupportedCurrency };

interface RegionContextType {
  /** The specific detected or selected ISO country code (e.g. 'za', 'na', 'us', 'gb', 'global') */
  regionCode: string;
  /** Active pricing region: 'za' | 'uk' | 'row' */
  region: SupportedRegion;
  /** Normalized operational region code ('za', 'us', 'gb', or 'global') */
  effectiveRegion: string;
  /** True if visitor is in South Africa or neighboring ZAR countries */
  isSouthernAfrica: boolean;
  /** True exclusively if visitor is in South Africa */
  isSouthAfrica: boolean;
  /** True if pricing displays in ZAR for this region (ZA, NA, BW, LS, SZ, ZW, MZ, ZM, MW) */
  isZar: boolean;
  /** True if 15% South African VAT applies (ZA exclusively) */
  vatApplies: boolean;
  /** Active currency: 'ZAR' | 'GBP' | 'USD' */
  currency: SupportedCurrency;
  /** Active currency symbol: 'R' | '£' | '$' */
  currencySymbol: string;
  setRegionCode: (code: string) => void;
  content: RegionalContent | null;
  loading: boolean;
  locale: string;
  formatDate: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
}

export const getLocaleConfig = (urlOrPath?: string) => {
  const path = urlOrPath || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const match = path.match(/^\/(en[-_]US|en[-_]GB|en[-_]ZA)/i);
  if (match) {
    const rawMatch = match[1].toLowerCase().replace('-', '_');
    let locale = 'en-GB';
    let region = 'gb';
    if (rawMatch === 'en_us') { locale = 'en-US'; region = 'us'; }
    if (rawMatch === 'en_gb') { locale = 'en-GB'; region = 'gb'; }
    if (rawMatch === 'en_za') { locale = 'en-GB'; region = 'za'; }
    return { basename: `/${match[1]}`, locale, regionCodeOverride: region };
  }
  return { basename: '/', locale: 'en-GB', regionCodeOverride: null };
};

const RegionContext = createContext<RegionContextType>({} as RegionContextType);

export function RegionProvider({ 
  children, 
  serverUrl, 
  initialGeo 
}: { 
  children: React.ReactNode; 
  serverUrl?: string; 
  initialGeo?: any;
}) {
  const { locale: initialLocale, regionCodeOverride } = getLocaleConfig(serverUrl);
  
  const [pricingState, setPricingState] = useState<{
    region: SupportedRegion;
    currency: SupportedCurrency;
    vat: boolean;
    country: string;
  }>(() => {
    if (regionCodeOverride) {
      return resolveRegion(regionCodeOverride, regionCodeOverride);
    }
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlRegion = params.get('region');
      const urlCountry = params.get('country');
      if (urlRegion || urlCountry) {
        return resolveRegion(urlCountry, urlRegion);
      }
      if (window.__GEO_INITIAL__?.country) {
        return resolveRegion(window.__GEO_INITIAL__.country, null);
      }
    }
    return resolveRegion(initialGeo?.country || null, null);
  });

  const [regionCode, setRegionCode] = useState<string>(() => {
    return pricingState.country.toLowerCase();
  });

  const [content, setContent] = useState<RegionalContent | null>(null);
  const [loading, setLoading] = useState(false);

  // Compute region, VAT, and currency status
  const isSouthAfrican = useMemo(() => pricingState.country.toUpperCase() === 'ZA', [pricingState.country]);
  const isZar = useMemo(() => pricingState.region === 'za', [pricingState.region]);
  const vatApplies = useMemo(() => pricingState.vat, [pricingState.vat]);
  const isNorthAmerican = useMemo(() => isNorthAmerica(regionCode), [regionCode]);

  const effectiveRegion = useMemo(() => {
    if (isSouthAfrican) return 'za';
    if (isNorthAmerican) return 'us';
    if (pricingState.region === 'uk' || regionCode === 'gb') return 'gb';
    return regionCode || 'global';
  }, [regionCode, isSouthAfrican, isNorthAmerican, pricingState.region]);

  // UK English globally, EXCEPT in North America (which displays US English)
  const activeLocale = useMemo(() => {
    if (isNorthAmerican) return 'en-US';
    return 'en-GB';
  }, [isNorthAmerican]);

  const currency: SupportedCurrency = pricingState.currency;
  const currencySymbol = useMemo(() => {
    if (pricingState.currency === 'GBP') return '£';
    if (pricingState.currency === 'ZAR') return 'R';
    return '$';
  }, [pricingState.currency]);

  // Apply strict language rule to i18next
  useEffect(() => {
    const langKey = activeLocale === 'en-US' ? 'en_us' : 'en_gb';
    i18n.changeLanguage(langKey).catch(() => {
      i18n.changeLanguage('en_gb');
    });
  }, [activeLocale]);

  // Client-side geolocation resolution using /api/public/region (never cached)
  useEffect(() => {
    if (regionCodeOverride) {
      const resolved = resolveRegion(regionCodeOverride, regionCodeOverride);
      setPricingState(resolved);
      setRegionCode(resolved.country.toLowerCase());
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const urlRegion = params.get('region');
    const urlCountry = params.get('country');
    if (urlRegion || urlCountry) {
      const resolved = resolveRegion(urlCountry, urlRegion);
      setPricingState(resolved);
      setRegionCode(resolved.country.toLowerCase());
      return;
    }

    // Always fetch un-cached /api/public/region to get exact edge IP-detected region
    fetch('/api/public/region')
      .then(res => res.json())
      .then(data => {
        if (data && data.region) {
          setPricingState({
            region: data.region,
            currency: data.currency,
            vat: Boolean(data.vat),
            country: data.country || 'US',
          });
          setRegionCode((data.country || data.region).toLowerCase());
        }
      })
      .catch((err) => {
        console.warn('Error fetching region:', err);
      });
  }, [regionCodeOverride]);

  useEffect(() => {
    if (!regionCode) return;
    
    // Switch i18n language: UK English globally, EXCEPT North America (US English)
    if (isNorthAmerican) {
      i18n.changeLanguage('en_us');
    } else {
      i18n.changeLanguage('en_gb');
    }

    let isMounted = true;
    setLoading(true);
    const fetchContent = async () => {
      try {
        const res = await fetch(`/api/public/regional-content?region=${encodeURIComponent(regionCode)}`);
        if (res.ok) {
          const data = await res.json();
          if (data && isMounted) {
            setContent(data as RegionalContent);
            return;
          }
        }
        if (isMounted) setContent(null);
      } catch (e) {
        console.warn('Error fetching regional content:', e);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchContent();
    return () => { isMounted = false; };
  }, [regionCode, isNorthAmerican, isZar]);

  const formatDate = (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => {
    const d = new Date(date);
    const defaultOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      ...options
    };
    return new Intl.DateTimeFormat(activeLocale, defaultOptions).format(d);
  };

  return (
    <RegionContext.Provider value={{
      regionCode,
      region: pricingState.region,
      effectiveRegion,
      isSouthernAfrica: pricingState.region === 'za',
      isSouthAfrica: isSouthAfrican,
      isZar: pricingState.region === 'za',
      vatApplies,
      currency,
      currencySymbol,
      setRegionCode,
      content,
      loading,
      locale: activeLocale,
      formatDate
    }}>
      {children}
    </RegionContext.Provider>
  );
}

export const useRegion = () => useContext(RegionContext);
